import { plans } from '@constants/plans'
import {
  legacyPlans,
  oldPlans,
  defaultLegacyPlans,
} from '@constants/plans/legacySpacePlans'
import {
  developmentModePlan,
  PLGPlans,
  SLGPlans,
} from '@constants/plans/newSpacePlans'
import type { LimitedSpace, Space } from '@/types'
import { isEmpty } from '@utils/checkers'

/**
 * Returns if the space is a old plan or not according to the space plan name
 *
 * @param  {string} planName - The plan name
 * @return {boolean}
 */
export const isOldSpacePlan = (planName: string): boolean => {
  return oldPlans.some((name) => planName.toLowerCase().startsWith(name))
}

/**
 * Returns if the space is a default plan or not according to the space plan name
 *
 * @param  {string} planName - The plan name
 * @return {boolean}
 */

export const isDefaultLegacySpacePlan = (planName: string): boolean => {
  return defaultLegacyPlans.includes(
    planName.toLowerCase() as (typeof defaultLegacyPlans)[number],
  )
}

/*
Returns if the space is a free plan or not according to the space plan level
This is the case for the Entry, Community, Teams and Starter plans
@param {object} space - The space object
@returns {boolean}
*/
export const isLegacyFreeSpacePlan = (space: Space): boolean => {
  return (
    space.plan_level === plans.community.level ||
    space.plan_level === plans.starter.level
  )
}

/*
Returns if the space is a legacy plan or not according to the space plan level
@param {object} space - The space object
@returns {boolean}
*/
export const isLegacyPlan = (space: Space): boolean => {
  return Object.values(legacyPlans).some(
    (plan) => plan.level === space.plan_level,
  )
}

/**
 * Returns if the space is under an Organization plan or not
 * All Enterprise plans must be associated to an Organization
 *
 * @param {Object} space - The space object
 * @returns {boolean}
 */
export const isOrgSpace = (space: Space | LimitedSpace): boolean => {
  if ('org' in space) {
    return !isEmpty(space.org)
  }

  return !!space?.org_id
}

/**
 * Formerly known as Starter plan, it refer to those space that were initially created WITHOUT a plan
 * where plan_level was 0 or null or undefined
 *
 * @param {object} space
 * @returns {boolean} It returns if the space is in a initial status with no plan subscribed yet
 */
export const isSpaceWithoutPlan = (space: Space | LimitedSpace): boolean => {
  return !space.plan_level // this means we are considering plans with level 0 but also null or not defined
}

export const isDemo = (space: Space): boolean => {
  return Boolean(space.is_demo)
}

/**
 * The following are ALL CURRENT ACTIVE legacyPlans
 * These assertion are based on this comment on Jira: https://storyblok.atlassian.net/browse/SD-4497?focusedCommentId=99069
 * that until things change could be considered the source of truth
 * Everything is based on the plan_level field instead of the name of the plan
 */

/**
 * Returns if the space is under a Development Mode (AKA Starter) plan or not according to the space plan level
 * This plan is only available for spaces that are associated to a Partner
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */

/*
    TODO in a separate PR - rename this method to is DevelopmentMode instead of isDevelopmentModePlan
     because there is now a new dedicated plan that should be treated differently than the 'dev mode'
   */
export const isDevelopmentModePlan = (space: Space | LimitedSpace): boolean =>
  Boolean(space.development_mode)

/**
 * Returns if the space is under a Community or the new Starter plan or not according to the space plan level
 * Type: Self-Service Plan
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isLowestSelfServicePlan = (space: Space): boolean =>
  space.plan_level === plans.community.level ||
  space.plan_level === plans.starter_2025.level

/**
 * Returns if the space is under a Community or the new Starter plan without Credit Card
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isLowestSelfServiceWithoutCC = (space: Space): boolean =>
  isLowestSelfServicePlan(space) && Boolean(space.no_cc_community)

/**
 * @deprecated use isLowestSelfServiceWithoutCC instead
 */
export const isCommunityWithoutCC = (space: Space): boolean =>
  space.plan_level === plans.community.level && Boolean(space.no_cc_community)

/**
 * Returns if the space is under a Entry plan or not according to the space plan level
 * Type: Self-Service Plan
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isEntryPlan = (space: Space): boolean =>
  space.plan_level === plans.entry.level

/**
 * Returns if the space is under a Business plan or not according to the space plan level
 * Type: Self-Service Plan
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isBusinessPlan = (space: Space): boolean =>
  space.plan_level === plans.business.level

/**
 * Returns if the space is under a Enterprise plan or not according to the space plan level
 * Type: Organization Plan
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isEnterprisePlan = (space: Space): boolean =>
  isOrgSpace(space) && space.plan_level === plans.enterprise.level

/**
 * Returns if the space is under a Enterprise Plus plan or not according to the space plan level
 * Type: Organization Plan
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isEnterprisePlusPlan = (space: Space): boolean =>
  isOrgSpace(space) && space.plan_level === plans.enterprise_plus.level

/**
 * Helper function to check if the space is under the highest Self-Service plan
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */

/**
 * Helper function to check if the space is associated with an organization and if its plan level
 * matches one of the specific Enterprise plans that include a space type:
 * - Enterprise Essentials
 * - Enterprise Scale
 * - Enterprise Ultimate
 *
 * @param {Space} space - The space object to evaluate.
 * @returns {boolean} - Returns `true` if the space is under an Enterprise plan with a space type, otherwise `false`.
 */
export const isEnterpriseWithSpaceType = (space: Space): boolean => {
  return (
    isOrgSpace(space) &&
    (space.plan_level === plans.enterprise_essentials.level ||
      space.plan_level === plans.enterprise_scale.level ||
      space.plan_level === plans.enterprise_ultimate.level)
  )
}

export const isTheHighestSelfServicePlan = (space: Space): boolean => {
  const selfServicePlans = Object.entries(plans).reduce(
    (acc, currentValue) => {
      return currentValue[1].self_service ? [...acc, currentValue[1]] : acc
    },
    [] as (typeof plans)[keyof typeof plans][],
  )

  return (
    space.plan_level ===
    selfServicePlans.sort((a, b) => b.level - a.level)[0]?.level
  )
}

/**
 * Helper function to check if the space is under a plan that is below the Business plan
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isBelowBusinessPlan = (space: Space): boolean =>
  !isOrgSpace(space) && space.plan_level < plans.business.level

/**
 * Helper function to check if the space is under a plan that is the Business plan or higher
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isBusinessPlanOrHigher = (space: Space): boolean =>
  space.plan_level >= plans.business.level

/**
 * Helper function to check if the space is under a plan that is above the Enterprise plan
 *
 * TIP: you could also use directly `isOrgSpace` since all Enterprise plans are associated to an Organization
 *
 * @param {object} space - The space object
 * @returns {boolean}
 */
export const isEnterprisePlanOrHigher = (
  space: Space | LimitedSpace,
): boolean => isOrgSpace(space) && space.plan_level >= plans.enterprise.level

export const isEnterprisePlanLevelOrHigher = (
  space: Space | LimitedSpace,
): boolean => space.plan_level >= plans.enterprise.level

export const isPLGPlanLevel = (level: number): boolean => {
  return (
    level >= PLGPlans.starter_2025.level && level <= PLGPlans.growth_plus.level
  )
}

export const isPLGPlan = (space: Space): boolean =>
  !isOrgSpace(space) && isPLGPlanLevel(space.plan_level)

export const isSLGPlanLevel = (level: number): boolean => {
  return level >= SLGPlans.premium_2025.level && level <= SLGPlans.elite.level
}

export const isSLGPlan = (space: Space): boolean => {
  return isOrgSpace(space) && isSLGPlanLevel(space.plan_level)
}

export const isChineseSLGPlan = (space: Space): boolean =>
  space.plan_level === SLGPlans.premium_2025_cn.level ||
  space.plan_level === SLGPlans.elite_cn.level

export const isHighestPLGPlan = (space: Space): boolean =>
  space.plan_level === PLGPlans.growth_plus.level

export const isDevelopmentPlan = (space: Space | LimitedSpace): boolean =>
  space.plan_level === developmentModePlan.level

/*
 * This method will be used throughout the whole logic to determine if a space is under a new pricing plan
 * because the new limits should only apply to new plans
 */
export const isNewPricingPlan = (space: Space): boolean => {
  return isSLGPlan(space) || isPLGPlan(space) || isDevelopmentPlan(space)
}

export const isJoyridePlan = (space: Space | LimitedSpace): boolean =>
  space.plan_level === PLGPlans.joyride.level ||
  space.plan?.toLowerCase() === 'joyride'
